/**
 * BalanceGraph component module.
 * @module shared/components/BalanceGraph
 *
 */
import React from 'react';
import PropTypes from 'prop-types';
import Line from '../components/graphs/Line';
import {_openModal} from '../reducers/modals';
import {connect} from 'react-redux';
import moment from 'moment';
import useSWR from 'swr';
import {_getPeriods, getDefaultTimePeriod} from '../reducers/periods';
import debug from 'debug';
import {Column, DescriptiveText, Dropdown, Icon, LoadingSpinner, Row} from '../index';
import {formatter} from '../utils/utils';
import useQueryString from '../utils/useQueryString';
import lazy from 'lazy.js';
import {SizeMe} from 'react-sizeme';
import {withTheme} from 'styled-components/macro';

const currency = formatter('currency', {noDecimals: 1});
const changePercent = formatter('percent', {decimalPlaces: 1});

const log = debug('circleblack:widgets:BalanceGraph');

const breakpoints = {
  md: 600,
  xs: 310,
};

const BalanceGraph = ({containerID, periods, getPeriods, theme}) => {
  const {data} = useSWR(containerID && `/containers/${containerID}/balances`);
  const [endBalance, setEndBalance] = React.useState(0);
  const [startBalance, setStartBalance] = React.useState(0);
  const [balances, setBalances] = React.useState();

  React.useEffect(() => {getPeriods();}, []);

  const [period, setPeriod] = useQueryString('balancePeriod');
  const [timePeriod, setTimePeriod] = React.useState();

  const _periods = React.useMemo(() => data && data[0] && lazy(periods)
    .filter(p => p.id === 'MAX' || (p.start >= data[0].date && p.days < data.length))
    .map(p => p.id === 'MAX' ? {
      ...p,
      start: data[0].date,
      days: moment(data[data.length - 1].date, 'YYYY-MM-DD').diff(moment(data[0].date, 'YYYY-MM-DD'), 'd'),
    } : p)
    .sortBy('days')
    .toArray(), [data, periods]);

  React.useEffect(() => {
    if (_periods && data) {
      if (data[0]) {
        setTimePeriod(getDefaultTimePeriod(_periods.find(p => p.id === period), data[0].date));
      } else {
        setTimePeriod({});
      }
    }
  }, [_periods, data]);

  React.useEffect(() => {
    if (timePeriod) {
      setPeriod(timePeriod.id);
    }
  }, [timePeriod]);

  React.useEffect(() => {
    if (data && timePeriod) {
      if (data.length) {
        const periodBalances = data && data.filter(b => b.date >= timePeriod.start)
          .map(b => [moment(b.date, 'YYYY-MM-DD').valueOf(), b.value]);
        setBalances(periodBalances);
        setStartBalance(periodBalances[0][1]);
        setEndBalance(data[data.length - 1].value);
      } else {
        setBalances([]);
      }
    }
  }, [data, timePeriod]);

  log('rendering', data, balances);

  if (!balances) {
    return <LoadingSpinner/>;
  }

  const balanceChange = () => startBalance
    ? `${changePercent(((endBalance - startBalance) / startBalance) * 100)}`
    : '-%';

  const changeIcon = (width) => {
    if (!balances[0]) {
      return null;
    }
    let svg = 'changeZero';
    let color = '#797b83';
    if (endBalance > startBalance) {
      svg = 'changeUp';
      color = theme.dark ? '#99FA66' : '#79AF30';
    } else if (endBalance < startBalance) {
      svg = 'changeDown';
      color = theme.dark ? '#FFCC03' : '#CF790B';
    }
    return <Row justifySubRow={'space between'}>
      {width >= breakpoints.md && <DescriptiveText preset={'standard'}
                                                   width={'100px'}
                                                   color={'white'}
                                                   alignment={'left'}>
        {currency(startBalance)}
      </DescriptiveText>}
      <Row width={'fit-content'}>
        <Row width={width < breakpoints.xs ? '150px' : 'fit-content'}>
          <Icon svg={svg}
                color={color}
                width={width < breakpoints.xs ? '30px' : '20px'}
                height={width < breakpoints.xs ? '30px' : '20px'}
                margin={width < breakpoints.xs ? '10px' : '5px'}/>
        </Row>
        <DescriptiveText preset={'dataLabelLarge'}
                         alignment={'center'}
                         width={width < breakpoints.xs ? '150px' : 'fit-content'}
                         fontSize={'16px'}>
          {currency(endBalance - startBalance)}
        </DescriptiveText>
        <DescriptiveText preset={'dataLabelLarge'}
                         alignment={'center'}
                         width={width < breakpoints.xs ? '150px' : 'fit-content'}
                         margin={'5px 0 5px 0'}
                         fontSize={'16px'}>
          {width < breakpoints.xs ? balanceChange() : `(${balanceChange()})`}
        </DescriptiveText>
      </Row>
      {width >= breakpoints.md && <DescriptiveText preset={'standard'}
                                                   width={'100px'}
                                                   color={'white'}
                                                   alignment={'right'}>
        {currency(endBalance)}
      </DescriptiveText>}
    </Row>;
  };

  if (!data) {
    return <LoadingSpinner/>;
  }

  return <SizeMe monitorHeight={true}>
    {({size}) => <Column
      height={'100px'}
      alignSubRow={'stretch'}
      alignItems={'stretch'}
      layout={{grow: 1}}>
      {data && data.length > 1
        && <Row justifySubRow={'left'}>
          <Row width={'fit-content'}>
            {timePeriod && <Dropdown onChange={(name, value) => setTimePeriod(value.value)}
                                     width={'150px'}
                                     options={_periods.map(p => ({label: p.name, value: p}))}
                                     dataPoint={{
                                       name: 'timePeriod',
                                       value: {label: timePeriod.name, value: timePeriod},
                                     }}
                                     placeholder={'Select a Period'}/>}
            <DescriptiveText preset={'dataLabel'}
                             alignment={'center'}
                             fontSize={'12px'}
                             width={size.width < breakpoints.xs ? '150px' : 'fit-content'}
                             style={{whiteSpace: 'nowrap'}}>
              {formatter('date')(timePeriod.start)} - {formatter('date')(timePeriod.end)}
            </DescriptiveText>
          </Row>
        </Row> }
      {changeIcon(size.width)}
      {!(size.width < breakpoints.xs) && <Column layout={{grow: 1}} height={'100px'}>
        {balances && balances.length > 1 && <Line title={'Balance Graph'} data={balances}/>}
        {balances && balances.length <= 1 && <DescriptiveText>There is not yet enough data to display the historical
        balances</DescriptiveText>}
      </Column>}
    </Column>}
  </SizeMe>;
};

const mapStateToProps = state => ({
  periods: state.periods,
});

const mapDispatchToProps = (dispatch) => ({
  openModal: modalToOpen => dispatch(_openModal(modalToOpen)),
  getPeriods: () => dispatch(_getPeriods()),
});

export default withTheme(connect(mapStateToProps, mapDispatchToProps)(BalanceGraph));

BalanceGraph.propTypes = {
  openModal: PropTypes.func,
  containerID: PropTypes.number.isRequired,
  getPeriods: PropTypes.func.isRequired,
  periods: PropTypes.array.isRequired,
  theme: PropTypes.object,
};
